import { ReactNode, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { usePermissionsGate } from '@/hooks/usePermissionsGate';
import { useDriverTheme } from '@/hooks/useDriverTheme';
import RequiredPermissionsScreen from '@/components/RequiredPermissionsScreen';

/**
 * Mounted ABOVE the router. Until every required permission is granted,
 * the entire app — including login — is replaced by
 * `RequiredPermissionsScreen`. Re-enforcement is automatic via
 * `PermissionsGateProvider` (broadcasts, lifecycle, 5s polling).
 */
export default function AppPermissionsGate({ children }: { children: ReactNode }) {
  const { status } = usePermissionsGate();
  const { tokens } = useDriverTheme();
  const [spinnerExpired, setSpinnerExpired] = useState(false);

  useEffect(() => {
    if (status !== 'checking') { setSpinnerExpired(false); return; }
    const t = setTimeout(() => setSpinnerExpired(true), 1500);
    return () => clearTimeout(t);
  }, [status]);

  // Public routes that must be reachable without the native permissions gate
  // (legal pages and the unauthenticated account-deletion request flow).
  const publicPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublicRoute =
    publicPath === '/driver/privacy' ||
    publicPath === '/driver/terms' ||
    publicPath === '/driver/delete-account';

  if (isPublicRoute) {
    return <>{children}</>;
  }

  if (status === 'checking' && !spinnerExpired) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: tokens.bgPage }}>
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (status !== 'granted') {
    return <RequiredPermissionsScreen />;
  }

  return <>{children}</>;
}